/**
 * LevelUpChoices — two-step level-up upgrade system.
 *
 * Step 1: Player picks a CATEGORY (Attribute, Skill, Perk, or +Grid Tile).
 * Step 2: 3 random choices from that category are shown; player picks one.
 *
 * Selection uses weighted random (WeightedRandom framework primitive).
 * Each choice has a weight controlling how often it appears.
 * Higher weight = more common; lower weight = rarer and usually more powerful.
 */

import type { PlayerCharacter } from "../PlayerCharacter";
import { ALL_SKILLS } from "../skills/SkillCatalog";
import { ALL_PERKS } from "../skills/PerkCatalog";
import type { SkillDef } from "../skills/SkillDef";
import type { PerkDef } from "../skills/PerkDef";
import { weightedPickN, Weighted } from "../../../framework/random";
import { MONO } from "../shapes";

export type UpgradeCategory = "attribute" | "skill" | "perk" | "grid";

export interface CategoryOption {
  category: UpgradeCategory;
  name: string;
  description: string;
  color: string;
  icon: string;
}

export interface LevelUpChoice {
  category: UpgradeCategory;
  id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  apply: (player: PlayerCharacter) => void;
}

/** The 4 category buttons shown in step 1. */
export const CATEGORIES: CategoryOption[] = [
  {
    category: "attribute",
    name: "Attribute",
    description: "Raw stat boost",
    color: "#ef4444",
    icon: "A",
  },
  {
    category: "skill",
    name: "Skill",
    description: "Passive bonus",
    color: "#3b82f6",
    icon: "S",
  },
  {
    category: "perk",
    name: "Perk",
    description: "Unique effect",
    color: "#a855f7",
    icon: "P",
  },
  {
    category: "grid",
    name: "+Grid Tile",
    description: "Expand word grid",
    color: "#22c55e",
    icon: "+",
  },
];

// --- Choice definitions ---

interface ChoiceDef {
  category: UpgradeCategory;
  id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  /** Selection weight — higher = more likely to appear. */
  weight: number;
  apply: (player: PlayerCharacter) => void;
  filter?: (player: PlayerCharacter) => boolean;
  /** Dynamic description showing current stat values. */
  descFn?: (player: PlayerCharacter) => string;
}

// --- Attribute choices (direct stat boosts) ---

const ATTRIBUTE_CHOICES: ChoiceDef[] = [
  {
    category: "attribute", id: "maxHp_3", name: "+3 Max HP",
    description: "Increase maximum health by 3", color: "#22c55e",
    icon: "+",
    weight: 10,
    apply: (p) => { p.maxHp += 3; p.hp += 3; },
  },
  {
    category: "attribute", id: "maxHp_5", name: "+5 Max HP",
    description: "Increase maximum health by 5", color: "#22c55e",
    icon: "+",
    weight: 6,
    apply: (p) => { p.maxHp += 5; p.hp += 5; },
  },
  {
    category: "attribute", id: "attack_1", name: "+1 Attack",
    description: "All words deal +1 damage", color: "#ef4444",
    icon: "/",
    weight: 10,
    apply: (p) => { p.baseAttack += 1; },
  },
  {
    category: "attribute", id: "attack_2", name: "+2 Attack",
    description: "All words deal +2 damage", color: "#ef4444",
    icon: "//",
    weight: 3,
    apply: (p) => { p.baseAttack += 2; },
  },
  {
    category: "attribute", id: "maxAp", name: "+1 Max AP",
    description: "Gain an extra action point each turn", color: "#fbbf24",
    icon: "O",
    weight: 3,
    apply: (p) => { p.maxAp += 1; },
    filter: (p) => p.maxAp < 5,
  },
  {
    category: "attribute", id: "heal_full", name: "Full Heal",
    description: "Restore all HP", color: "#3b82f6",
    icon: "H",
    weight: 8,
    apply: (p) => { p.hp = p.maxHp; },
    filter: (p) => p.hp < p.maxHp,
  },
  {
    category: "attribute", id: "hp_atk", name: "+2 HP, +1 ATK",
    description: "A bit of both: +2 max HP and +1 attack", color: "#f59e0b",
    icon: "&",
    weight: 6,
    apply: (p) => { p.maxHp += 2; p.hp += 2; p.baseAttack += 1; },
  },
];

// --- Skill choices (generated from SkillCatalog) ---

function buildSkillChoices(): ChoiceDef[] {
  return ALL_SKILLS.map((def: SkillDef): ChoiceDef => ({
    category: "skill",
    id: def.id,
    name: def.name,
    description: def.description,
    color: def.color,
    icon: def.icon,
    weight: def.weight,
    apply: (p) => { p.skills.addSkill(def); },
    filter: (p) => {
      const inst = p.skills.getSkill(def.id);
      return !inst || inst.canLevelUp;
    },
    descFn: (p) => {
      const inst = p.skills.getSkill(def.id);
      if (inst) {
        const nextLevel = inst.level + 1;
        const nextDesc = def.descFn?.(nextLevel) ?? def.description;
        return `Lv${inst.level}\u2192${nextLevel}: ${nextDesc}`;
      }
      return def.descFn?.(1) ?? def.description;
    },
  }));
}

// --- Perk choices (generated from PerkCatalog) ---

function buildPerkChoices(): ChoiceDef[] {
  return ALL_PERKS.map((def: PerkDef): ChoiceDef => ({
    category: "perk",
    id: def.id,
    name: def.name,
    description: def.description,
    color: def.color,
    icon: def.icon,
    weight: def.weight,
    apply: (p) => { p.skills.addPerk(def); },
    filter: (p) => {
      const inst = p.skills.getPerk(def.id);
      if (!inst) return true;
      return inst.canStack;
    },
    descFn: (p) => {
      const inst = p.skills.getPerk(def.id);
      if (inst) {
        if (def.stackable) {
          const nextStacks = inst.stacks + 1;
          const nextDesc = def.descFn?.(nextStacks) ?? def.description;
          return `x${inst.stacks}\u2192${nextStacks}: ${nextDesc}`;
        }
        return inst.description;
      }
      return def.descFn?.(1) ?? def.description;
    },
  }));
}

// --- Grid choice ---

const GRID_CHOICES: ChoiceDef[] = [
  {
    category: "grid", id: "grid_tile", name: "+1 Grid Tile",
    description: "Expand the letter grid by one tile", color: "#22c55e",
    icon: "+",
    weight: 10,
    apply: (p) => { p.shapes.add(MONO); },
  },
];

// --- Lookup ---

const SKILL_CHOICES = buildSkillChoices();
const PERK_CHOICES = buildPerkChoices();

const POOLS: Record<UpgradeCategory, ChoiceDef[]> = {
  attribute: ATTRIBUTE_CHOICES,
  skill: SKILL_CHOICES,
  perk: PERK_CHOICES,
  grid: GRID_CHOICES,
};

/**
 * Generate `count` random choices from a specific category.
 * Filters out choices that don't apply to the player's current state.
 * Uses weighted random selection — rarer choices appear less often.
 */
export function generateChoicesForCategory(
  category: UpgradeCategory,
  player: PlayerCharacter,
  count: number = 3,
): LevelUpChoice[] {
  const raw = POOLS[category];
  const pool = raw.filter((c) => !c.filter || c.filter(player));

  // Build weighted entries and pick N without replacement
  const weighted: Weighted<ChoiceDef>[] = pool.map(c => ({
    item: c,
    weight: c.weight,
  }));

  const picked = weightedPickN(weighted, count);

  return picked.map((c) => ({
    category: c.category,
    id: c.id,
    name: c.name,
    description: c.descFn ? c.descFn(player) : c.description,
    color: c.color,
    icon: c.icon,
    apply: c.apply,
  }));
}
